/**
 * <brass-astrolabe> — ambient soundtrack player with multi-track support.
 *
 * Loads N perfectly-aligned stems of a Brass Astrolabe composition, plays
 * them in lockstep, looped. Each source stem can publish ONE OR MORE
 * visual channels — the level loop samples the stem's spectrum and slices
 * it into frequency bands, each band publishing its own CSS variable:
 *
 *     :root {
 *       --st-lead-vocals-lvl: 0;
 *       --st-backing-vocals-lvl: 0;
 *       --st-drums-lvl: 0;
 *       --st-bass-lvl: 0;
 *       --st-guitar-lvl: 0;
 *       --st-percussion-lvl: 0;
 *       --st-strings-lvl: 0;
 *       --st-synth-lvl: 0;
 *       --st-overall-lvl: 0;
 *       --st-peak: 0;
 *     }
 *
 * The 77 BPM master mix ships with 8 dedicated stems (1:1 with channels).
 * The 132 BPM mix ships with 5 stems; the "drums" stem fans out to drums
 * (low-mid) + percussion (high), and "other" fans out to bass (low) +
 * guitar (mid) + synth (high). Every visual effect stays alive on both.
 *
 * Attributes:
 *   track="77bpm" | "132bpm"        which composition to load
 *   volume="0.6"                    master volume 0-1
 *   muted=""                        presence = mute (soundtrack switched off)
 *   dormant=""                      presence = a meditation is playing
 *
 * MUTED and DORMANT are power states, not just gain states:
 *
 *   muted   — the user switched the soundtrack OFF in Settings. We now
 *             refuse to build the graph at all. Previously `muted` only
 *             ramped masterGain to 0, so a user who had turned the
 *             soundtrack off was STILL downloading the stems, decoding
 *             them on loop forever, running an FFT, and writing eleven
 *             CSS custom properties to :root 20-60× a second for the
 *             entire session. Silent, invisible, and the single largest
 *             standing power draw in the app.
 *
 *   dormant — Lisa's voice is playing. Whatever is already sounding keeps
 *             sounding (the bed is part of the experience), but every
 *             non-audible cost stops: no FFT sweep, no :root writes, no
 *             drift resync. And if the bed was NOT already running we do
 *             not spin it up mid-meditation — never start a second
 *             decoder underneath the voice.
 *
 * Browser autoplay rules require a user gesture before AudioContext can
 * resume. We expose a `start()` method (also auto-bound to the first
 * pointerdown/keydown on document) that resumes the context and starts
 * playback in lockstep.
 *
 * All stems are identical length; we re-sync every 8s by snapping the
 * currentTime of every stem to the median value — defends against drift.
 */

type Channel =
  | "lead-vocals"
  | "backing-vocals"
  | "drums"
  | "bass"
  | "guitar"
  | "percussion"
  | "strings"
  | "synth";

const ALL_CHANNELS: readonly Channel[] = [
  "lead-vocals",
  "backing-vocals",
  "drums",
  "bass",
  "guitar",
  "percussion",
  "strings",
  "synth",
] as const;

/** Publisher = one CSS variable derived from a stem's spectrum, optionally
 *  band-limited to a contiguous range of FFT bins. */
type Publisher = {
  readonly channel: Channel;
  readonly binStart?: number;     // default 2 (skip rumble)
  readonly binEnd?: number;       // default 64 (skip hiss)
  readonly gain?: number;         // multiplier so band slices read at parity
};

type SourceStem = {
  readonly file: string;          // file stem (no extension)
  readonly defaultGain: number;
  readonly publishers: readonly Publisher[];
};

type TrackConfig = {
  readonly base: string;          // URL prefix for stem files
  readonly bpm: number;
  readonly stems: readonly SourceStem[];
};

type TrackKey = "77bpm" | "132bpm" | "77bpm-master" | "132bpm-master";

/** Band-split publishers for the single premixed master files (phone tiers).
 *  One analyser slices the full mix into the same 8 visual channels the
 *  dedicated stems drive, so every audio-reactive surface stays alive while
 *  decode cost drops from 8 (or 5) parallel opus streams to one. Bands
 *  overlap deliberately — each channel keys on the spectral region its
 *  instrument dominates. */
const MASTER_PUBLISHERS: readonly Publisher[] = [
  { channel: "bass",           binStart: 2,  binEnd: 10, gain: 1.5 },
  { channel: "drums",          binStart: 4,  binEnd: 24, gain: 1.2 },
  { channel: "lead-vocals",    binStart: 12, binEnd: 32, gain: 1.2 },
  { channel: "guitar",         binStart: 18, binEnd: 40, gain: 1.2 },
  { channel: "backing-vocals", binStart: 24, binEnd: 44, gain: 1.3 },
  { channel: "strings",        binStart: 30, binEnd: 52, gain: 1.3 },
  { channel: "synth",          binStart: 40, binEnd: 60, gain: 1.5 },
  { channel: "percussion",     binStart: 48, binEnd: 64, gain: 1.6 },
];

const TRACKS: Readonly<Record<TrackKey, TrackConfig>> = {
  // Premixed single-file variants — selected automatically on mid/low tier.
  // The mix bakes in each stem's defaultGain (rendered offline via ffmpeg).
  "77bpm-master": {
    base: "/soundtrack",
    bpm: 77,
    stems: [{ file: "master", defaultGain: 1.0, publishers: MASTER_PUBLISHERS }],
  },
  "132bpm-master": {
    base: "/soundtrack/132bpm",
    bpm: 132,
    stems: [{ file: "master", defaultGain: 1.0, publishers: MASTER_PUBLISHERS }],
  },
  // Original 8-stem master mix — 1:1 stem → channel mapping.
  "77bpm": {
    base: "/soundtrack",
    bpm: 77,
    stems: [
      { file: "lead-vocals",    defaultGain: 0.55, publishers: [{ channel: "lead-vocals" }] },
      { file: "backing-vocals", defaultGain: 0.45, publishers: [{ channel: "backing-vocals" }] },
      { file: "drums",          defaultGain: 0.55, publishers: [{ channel: "drums" }] },
      { file: "bass",           defaultGain: 0.55, publishers: [{ channel: "bass" }] },
      { file: "guitar",         defaultGain: 0.55, publishers: [{ channel: "guitar" }] },
      { file: "percussion",     defaultGain: 0.50, publishers: [{ channel: "percussion" }] },
      { file: "strings",        defaultGain: 0.65, publishers: [{ channel: "strings" }] },
      { file: "synth",          defaultGain: 0.55, publishers: [{ channel: "synth" }] },
    ],
  },

  // 132 BPM stripped-down mix — 5 stems fanned out into 8 visual channels
  // via frequency-band splits on "drums" and "other". This keeps every
  // music-juice effect alive without needing real per-instrument stems.
  "132bpm": {
    base: "/soundtrack/132bpm",
    bpm: 132,
    stems: [
      { file: "lead-vocals",    defaultGain: 0.55, publishers: [{ channel: "lead-vocals" }] },
      { file: "backing-vocals", defaultGain: 0.45, publishers: [{ channel: "backing-vocals" }] },
      { file: "drums", defaultGain: 0.60, publishers: [
        // Kick + snare body — low-mid bins drive the drums channel.
        { channel: "drums",      binStart: 2,  binEnd: 28, gain: 1.0 },
        // Cymbals + hi-hat — high bins drive percussion.
        { channel: "percussion", binStart: 28, binEnd: 64, gain: 1.4 },
      ]},
      { file: "strings",        defaultGain: 0.65, publishers: [{ channel: "strings" }] },
      { file: "other", defaultGain: 0.55, publishers: [
        // Sub bass — bottom 10 bins.
        { channel: "bass",   binStart: 2,  binEnd: 12, gain: 1.4 },
        // Mid-range — guitar / pad body.
        { channel: "guitar", binStart: 12, binEnd: 40, gain: 1.1 },
        // Upper-mids + air — synth pads.
        { channel: "synth",  binStart: 40, binEnd: 64, gain: 1.5 },
      ]},
    ],
  },
};

import { getAudioFftSize, getDeviceTier } from "./perf-budget.ts";
import { publishStems, type StemLevels } from "./frame-bus.ts";

const LEVEL_SMOOTHING = 0.18;
const RESYNC_INTERVAL_MS = 8_000;
const FFT_SIZE = getAudioFftSize();    // 128 on low-tier, 256 otherwise

/** Level-loop cadence by tier. The loop's FFT pull + :root var writes
 *  invalidate style document-wide, so on phones we run the WHOLE loop at
 *  a reduced rate (not just the stem writes). 30/20 Hz is still far above
 *  the ~100ms audio→visual perceptual threshold. */
const LOOP_INTERVAL_MS = ((): number => {
  const tier = getDeviceTier();
  return tier === "high" ? 0 : tier === "mid" ? 33 : 50;
})();
const STEM_PUBLISH_MS_BY_TIER = getDeviceTier() === "high" ? 50 : 100;

type ChannelState = {
  readonly channel: Channel;
  smoothedLevel: number;
};

type StemRig = {
  readonly key: string;           // source file stem (for diagnostics)
  readonly audio: HTMLAudioElement;
  readonly source: MediaElementAudioSourceNode;
  readonly gainNode: GainNode;
  readonly analyser: AnalyserNode;
  readonly buffer: Uint8Array<ArrayBuffer>;
  readonly publishers: readonly Publisher[];
};

class BrassAstrolabe extends HTMLElement {
  #ctx: AudioContext | null = null;
  #masterGain: GainNode | null = null;
  #rigs: StemRig[] = [];
  #channelStates: Map<Channel, ChannelState> = new Map();
  #rafId: number | null = null;
  #resyncTimer: ReturnType<typeof setInterval> | null = null;
  #started: boolean = false;
  #pendingStart: boolean = false;
  #currentTrack: TrackKey | null = null;
  #unbindGesture: (() => void) | null = null;

  static get observedAttributes(): readonly string[] {
    return ["volume", "muted", "track", "dormant"];
  }

  /** User switched the soundtrack off in Settings. */
  get #isMuted(): boolean {
    return this.hasAttribute("muted");
  }

  /** A meditation is playing — see the header note. */
  get #isDormant(): boolean {
    return this.hasAttribute("dormant");
  }

  connectedCallback(): void {
    const handler = (): void => {
      if (this.#started) return;
      this.#pendingStart = true;
      void this.start();
    };
    document.addEventListener("pointerdown", handler, { once: true, capture: true });
    document.addEventListener("keydown", handler, { once: true, capture: true });
    document.addEventListener("touchstart", handler, { once: true, capture: true });
    this.#unbindGesture = (): void => {
      document.removeEventListener("pointerdown", handler, { capture: true });
      document.removeEventListener("keydown", handler, { capture: true });
      document.removeEventListener("touchstart", handler, { capture: true });
    };
  }

  disconnectedCallback(): void {
    this.#unbindGesture?.();
    this.#unbindGesture = null;
    this.stop();
    this.#stopLevelLoop();
    this.#stopResyncTimer();
  }

  attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
    if (name === "volume" || name === "muted" || name === "dormant") {
      if (this.#masterGain && this.#ctx) this.#applyVolume();

      if (this.#isMuted) {
        // Switched off. Silence AND stillness — pause the decoders too, so
        // "off" actually costs nothing. #pendingStart lets it come back.
        if (this.#started) {
          this.stop();
          this.#pendingStart = true;
        }
        this.#quiesce();
      } else if (this.#isDormant) {
        // Lisa's voice took over. Keep sounding, stop everything else.
        this.#quiesce();
      } else if (this.#started) {
        // Back to the compass — bring the visuals back to life.
        this.#startLevelLoop();
        this.#startResyncTimer();
      }

      // A start deferred by mute/dormancy resumes the moment both clear.
      // This is also the path that first builds the graph for a user who
      // had the soundtrack off when the page loaded and turned it on later.
      if (this.#pendingStart && !this.#isMuted && !this.#isDormant) {
        void this.start();
      }
      return;
    }
    if (name === "track") {
      const next = this.#resolveTrack(value);
      if (next !== this.#currentTrack && this.#ctx) {
        void this.#switchTrack(next);
      }
    }
  }

  #resolveTrack(raw: string | null): TrackKey {
    const base = ((): "77bpm" | "132bpm" => {
      if (raw === "132bpm" || raw === "77bpm") return raw;
      if (raw === "random") {
        // Cache the random pick per page load so route changes don't keep
        // switching mid-session. Stored on the element itself.
        const cached = this.dataset["resolvedTrack"];
        if (cached === "132bpm" || cached === "77bpm") return cached;
        const pick = Math.random() < 0.5 ? ("77bpm" as const) : ("132bpm" as const);
        this.dataset["resolvedTrack"] = pick;
        return pick;
      }
      return "77bpm";
    })();
    // Phone tiers get the premixed single file: one decoder + one analyser
    // instead of 8 (or 5) parallel opus streams — the difference between a
    // warm pocket and a hot one over a 20-minute meditation.
    return getDeviceTier() === "high" ? base : (`${base}-master` as TrackKey);
  }

  async start(): Promise<void> {
    if (this.#started) return;
    // Refuse to build (or resume) the graph while switched off or while a
    // meditation is playing. #pendingStart is left set so the deferred
    // start fires from attributeChangedCallback once the state clears.
    if (this.#isMuted || this.#isDormant) {
      this.#pendingStart = true;
      return;
    }
    if (!this.#ctx) await this.#setup();
    if (!this.#ctx || !this.#masterGain) return;
    if (this.#ctx.state === "suspended") await this.#ctx.resume();
    for (const r of this.#rigs) r.audio.currentTime = 0;
    await Promise.all(this.#rigs.map((r) => r.audio.play().catch(() => undefined)));
    this.#started = true;
    this.#pendingStart = false;
    this.#applyVolume();
    this.#startLevelLoop();
    this.#startResyncTimer();
  }

  stop(): void {
    for (const r of this.#rigs) r.audio.pause();
    this.#started = false;
  }

  async #setup(): Promise<void> {
    type AudioContextWindow = Window & { webkitAudioContext?: typeof AudioContext };
    const Ctor = window.AudioContext ?? (window as AudioContextWindow).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    this.#ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    this.#masterGain = master;
    const track = this.#resolveTrack(this.getAttribute("track"));
    await this.#loadTrack(track);
  }

  async #loadTrack(track: TrackKey): Promise<void> {
    if (!this.#ctx || !this.#masterGain) return;
    const cfg = TRACKS[track];
    this.#currentTrack = track;
    // Seed channel state for ALL eight channels so initial CSS reads
    // don't return empty strings.
    this.#channelStates.clear();
    for (const ch of ALL_CHANNELS) {
      this.#channelStates.set(ch, { channel: ch, smoothedLevel: 0 });
    }
    const ctx = this.#ctx;
    const master = this.#masterGain;
    await Promise.all(
      cfg.stems.map(async (s) => {
        const audio = new Audio(`${cfg.base}/${s.file}.opus`);
        audio.crossOrigin = "anonymous";
        audio.loop = true;
        audio.preload = "auto";
        audio.muted = false;
        await new Promise<void>((resolve) => {
          audio.addEventListener("canplaythrough", () => resolve(), { once: true });
          audio.addEventListener("error", () => resolve(), { once: true });
          audio.load();
        });
        const source = ctx.createMediaElementSource(audio);
        const gainNode = ctx.createGain();
        gainNode.gain.value = s.defaultGain;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        analyser.smoothingTimeConstant = 0.65;
        source.connect(gainNode).connect(analyser).connect(master);
        const buffer = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
        this.#rigs.push({
          key: s.file,
          audio,
          source,
          gainNode,
          analyser,
          buffer,
          publishers: s.publishers,
        });
      }),
    );
  }

  async #switchTrack(next: TrackKey): Promise<void> {
    if (!this.#ctx) return;
    const wasPlaying = this.#started;
    // Tear down current rigs.
    for (const r of this.#rigs) {
      r.audio.pause();
      try { r.source.disconnect(); } catch { /* already detached */ }
      try { r.gainNode.disconnect(); } catch { /* */ }
      try { r.analyser.disconnect(); } catch { /* */ }
      r.audio.src = "";
    }
    this.#rigs = [];
    this.#started = false;
    await this.#loadTrack(next);
    if (wasPlaying) await this.start();
  }

  #applyVolume(): void {
    if (!this.#ctx || !this.#masterGain) return;
    const muted = this.hasAttribute("muted");
    const rawVol = parseFloat(this.getAttribute("volume") ?? "0.6");
    const target = muted || !Number.isFinite(rawVol) ? 0 : Math.max(0, Math.min(1, rawVol));
    const t = this.#ctx.currentTime;
    this.#masterGain.gain.cancelScheduledValues(t);
    this.#masterGain.gain.linearRampToValueAtTime(target, t + 0.20);
  }

  /** Sample an analyser buffer over a contiguous bin range and return
   *  the RMS level normalised to 0-1 (with optional gain). */
  #rmsBand(buffer: Uint8Array, binStart: number, binEnd: number, gain: number): number {
    const len = buffer.length;
    const lo = Math.max(0, Math.min(len, binStart));
    const hi = Math.max(lo, Math.min(len, binEnd));
    let sumSq = 0;
    let count = 0;
    for (let i = lo; i < hi; i += 1) {
      const v = (buffer[i] ?? 0) / 255;
      sumSq += v * v;
      count += 1;
    }
    const rms = count > 0 ? Math.sqrt(sumSq / count) : 0;
    return Math.min(1, rms * 1.6 * gain);
  }

  #startLevelLoop(): void {
    if (this.#rafId !== null) return;
    const root = document.documentElement;
    // CSS variable writes invalidate style for every descendant — costly
    // on large trees. We split publishing into two cadences:
    //
    //   RAF (60 Hz): --st-overall-lvl, --st-peak, --st-loud (3 writes)
    //     → drive global bloom, BG inverse-blur, peak white-hot.
    //     These are what users perceive as "responsive to the beat".
    //
    //   30 Hz tick: 8 per-stem CSS vars (--st-{stem}-lvl)
    //     → drive specific ring/bloom/glow on each compass element.
    //     30 Hz is well above human perception of audio→visual lag
    //     (≈100 ms perceptual threshold) and halves the cascade cost.
    //
    // FFT pull stays on RAF because the analyser data needs to be fresh
    // for both loops; the smoothedLevel state is shared.
    let stemPublishAcc = 0;             // ms accumulated since last stem write
    const STEM_PUBLISH_MS = STEM_PUBLISH_MS_BY_TIER;
    let lastTickAt = performance.now();
    let lastRunAt = 0;                  // tier-throttled loop gate

    const tick = (now: number): void => {
      // Phone tiers run the whole loop (FFT pull + var writes + bus publish)
      // at 30/20 Hz — every skipped frame is one less document-wide style
      // invalidation and one less analyser sweep.
      if (LOOP_INTERVAL_MS > 0 && now - lastRunAt < LOOP_INTERVAL_MS) {
        this.#rafId = requestAnimationFrame(tick);
        return;
      }
      lastRunAt = now;
      const dt = now - lastTickAt;
      lastTickAt = now;
      stemPublishAcc += dt;

      const incoming: Map<Channel, number> = new Map();
      for (const r of this.#rigs) {
        r.analyser.getByteFrequencyData(r.buffer);
        for (const pub of r.publishers) {
          const lvl = this.#rmsBand(
            r.buffer,
            pub.binStart ?? 2,
            pub.binEnd ?? 64,
            pub.gain ?? 1.0,
          );
          const prev = incoming.get(pub.channel) ?? 0;
          if (lvl > prev) incoming.set(pub.channel, lvl);
        }
      }

      // Smooth every channel every frame (state needs to track real loudness),
      // but only WRITE the per-stem CSS vars at the throttled cadence.
      // Frame-bus values are updated EVERY frame so JS-side consumers
      // (canvas particle systems) always see live data — DOM writes are
      // the only expensive part.
      let maxLevel = 0;
      let sumLevel = 0;
      let publishedCount = 0;
      const shouldPublishStems = stemPublishAcc >= STEM_PUBLISH_MS;
      const busSnapshot: Record<string, number> = {};
      for (const ch of ALL_CHANNELS) {
        const state = this.#channelStates.get(ch);
        if (!state) continue;
        const target = incoming.get(ch) ?? 0;
        state.smoothedLevel = state.smoothedLevel + (target - state.smoothedLevel) * LEVEL_SMOOTHING;
        busSnapshot[ch] = state.smoothedLevel;
        if (shouldPublishStems) {
          root.style.setProperty(`--st-${ch}-lvl`, state.smoothedLevel.toFixed(3));
        }
        if (state.smoothedLevel > maxLevel) maxLevel = state.smoothedLevel;
        sumLevel += state.smoothedLevel;
        publishedCount += 1;
      }
      if (shouldPublishStems) stemPublishAcc = 0;

      // Aggregate vars publish every frame — these drive the most
      // perceptually-important global responses.
      const meanLevel = sumLevel / Math.max(1, publishedCount);
      const overall = Math.min(1, maxLevel * 0.7 + meanLevel * 0.6);
      root.style.setProperty("--st-overall-lvl", overall.toFixed(3));
      // PEAK — gate tightened further (was 0.60, now 0.72) + cubic curve
      // so peaks fire ONLY on true transients and fall off fast. Peaks
      // act as punctuation, not sustained washes. Still ramps to 1.0.
      const peakBase = Math.max(0, overall - 0.72) / 0.28;
      const peak = Math.min(1, peakBase * peakBase * peakBase * 2.0);
      root.style.setProperty("--st-peak", peak.toFixed(3));
      // LOUD — slightly more concave (was ^2.2 now ^2.6) so the BG stays
      // dreamy through more of the dynamic range.
      const loud = Math.min(1, Math.pow(overall, 2.6));
      root.style.setProperty("--st-loud", loud.toFixed(3));

      // Frame bus — JS-side consumers (canvas particle systems) read from
      // here instead of getComputedStyle, eliminating ~40 style-cascade
      // flushes per frame. CSS reactivity above still works via the
      // vars on :root; this is a parallel JS-only fast path.
      publishStems(busSnapshot as StemLevels, overall, peak, loud);

      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #stopLevelLoop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #stopResyncTimer(): void {
    if (this.#resyncTimer !== null) {
      clearInterval(this.#resyncTimer);
      this.#resyncTimer = null;
    }
  }

  /** Stop every non-audible cost and leave no stale reactive state behind:
   *  cancel the analyser loop and the drift resync, then publish one final
   *  zero so nothing is frozen mid-glow. Sound (if any) keeps playing. */
  #quiesce(): void {
    this.#stopLevelLoop();
    this.#stopResyncTimer();
    const root = document.documentElement;
    const zeroed: Record<string, number> = {};
    for (const ch of ALL_CHANNELS) {
      zeroed[ch] = 0;
      root.style.setProperty(`--st-${ch}-lvl`, "0");
    }
    root.style.setProperty("--st-overall-lvl", "0");
    root.style.setProperty("--st-peak", "0");
    root.style.setProperty("--st-loud", "0");
    publishStems(zeroed as StemLevels, 0, 0, 0);
  }

  #startResyncTimer(): void {
    if (this.#resyncTimer !== null) return;
    this.#resyncTimer = setInterval(() => {
      if (!this.#started || this.#rigs.length === 0) return;
      const times = this.#rigs.map((r) => r.audio.currentTime).sort((a, b) => a - b);
      const median = times[Math.floor(times.length / 2)] ?? 0;
      const maxDrift = Math.max(...times) - Math.min(...times);
      if (maxDrift > 0.03) {
        for (const r of this.#rigs) {
          if (Math.abs(r.audio.currentTime - median) > 0.015) {
            r.audio.currentTime = median;
          }
        }
      }
    }, RESYNC_INTERVAL_MS);
  }
}

export const registerBrassAstrolabe = (): void => {
  if (customElements.get("brass-astrolabe") !== undefined) return;
  customElements.define("brass-astrolabe", BrassAstrolabe);
};
